import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import { Label, TextField, SelectField, MultipleFields, Message as FormMessage, ButtonField } from '../../../components/hook-form';
import Dialog from '../../../components/dialog';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// utils
import { REGEXP_SPECIAL, REGEXP_DECIMAL } from '../../../utils/regexp';
// styles
import { Container, ButtonWrapper, PrimaryButton, TertiaryButton, Message } from '../../../styles';
// apis
import { existsSensorModel, createSensorModel, updateSensorModel } from '../../../apis';

// ----------------------------------------------------------------------

const schema = yup.object().shape({
  name: yup.string()
    .required()
    .trim()
    .max(20, '형식을 확인해 주세요.')
    .matches(REGEXP_SPECIAL, '형식을 확인해 주세요.'),
  model: yup.string()
    .required()
    .trim()
    .max(20, '형식을 확인해 주세요.')
    .matches(REGEXP_SPECIAL, '형식을 확인해 주세요.'),
  waterQualityItem: yup.string(),
  unit: yup.string()
    .required()
    .trim()
    .max(10, '10자 이내로 입력해 주세요.'),
  alarm: yup.string(),
  alarmLowerLimit: yup.number()
    .test('matches', '숫자, 소수점 3자리까지만 입력 가능합니다.', (value, context) => {
      if ( (context.parent.alarm === 'OUT_OF_RANGE' || context.parent.alarm === 'LOWER_LIMIT') && !REGEXP_DECIMAL.test(String(value)) ) return false;
      return true;
    }),
  alarmUpperLimit: yup.number()
    .test('matches', '숫자, 소수점 3자리까지만 입력 가능합니다.', (value, context) => {
      if ( (context.parent.alarm === 'OUT_OF_RANGE' || context.parent.alarm === 'UPPER_LIMIT') && !REGEXP_DECIMAL.test(String(value)) ) return false;
      return true;
    })
});

// ----------------------------------------------------------------------

interface Props {
  name?: string;
  model?: string;
  waterQualityItem?: string;
  unit?: string;
  alarm?: string;
  alarmLowerLimit?: number;
  alarmUpperLimit?: number;
};

// ----------------------------------------------------------------------

const ALARM: {[key: string]: string} = {
  '범위': 'OUT_OF_RANGE',
  '하한': 'LOWER_LIMIT',
  '상한': 'UPPER_LIMIT'
};

// ----------------------------------------------------------------------

export default function FormPage() {
  const navigate  = useNavigate();
  const create = useLocation().pathname.split('/')[3] === 'create' ? true : false;
  const state = useLocation().state;

  const {
    control,
    setValue,
    getValues,
    setFocus,
    setError,
    watch,
    formState,
    clearErrors,
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: state?.name || '',
      model: state?.model || '',
      waterQualityItem: state?.waterQualityItem || 'PH',
      unit: state?.unit || '',
      alarm: state ? ALARM[state.alarmType] : 'OUT_OF_RANGE',
      alarmLowerLimit: state && state?.alarmLowerLimit !== null ? state?.alarmLowerLimit : 0,
      alarmUpperLimit: state && state?.alarmUpperLimit !== null ? state?.alarmUpperLimit : 1
    }
  });

  useEffect(() => {
    if ( watch('alarm') === 'UPPER_LIMIT' ) clearErrors('alarmLowerLimit');
    if ( watch('alarm') === 'LOWER_LIMIT' ) clearErrors('alarmUpperLimit');
  }, [watch('alarm')]);

  const [ formError, setFormError ] = useState(null);
  useEffect(() => {
    if ( Object.values(formState.errors).filter((error) => error.type === 'matches').length > 0 ) return setFormError('숫자, 소수점 3자리까지만 입력 가능합니다.');
    setFormError(null);
  }, [formState]);

  useEffect(() => {
    if ( Object.values(formState.errors).length === 0 ) {
      if ( watch('alarm') === 'OUT_OF_RANGE' && watch('alarmLowerLimit') >= watch('alarmUpperLimit') ) {
        setFormError('범위 설정을 확인해 주세요.');
      } else {
        setFormError(null);
      }
    }
  }, [watch()]);

  const [ checked, setChecked ] = useState({result: null, message: null});
  const [ checkedInfo, setCheckedInfo ] = useState(null);
  const [ message, setMessage ] = useState(null);

  const [ dialog, setDialog ] = useState(false);
  const handleClose = () => setDialog(false);

  const handleChecked = async () => {
    const name = getValues('name').trim();
    const model = getValues('model').trim();

    if ( name === '' ) {
      setError('name', {type: 'required'});
      setFocus('name');
    } else if ( model === '' ) {
      setError('model', {type: 'required'});
      setFocus('model');
    }

    if ( name.length > 20 || !REGEXP_SPECIAL.test(name) ) {
      setError('name', {type: 'matches', message: '형식을 확인해 주세요.'});
      setFocus('name');
    }

    if ( model.length > 20 || !REGEXP_SPECIAL.test(model) ) {
      setError('model', {type: 'matches', message: '형식을 확인해 주세요.'});
      setFocus('model');
    }
    
    if ( name !== '' && name.length <= 20 && REGEXP_SPECIAL.test(name) ) clearErrors('name');
    if ( model !== '' &&  model.length <= 20 && REGEXP_SPECIAL.test(model) ) clearErrors('model');
    
    if ( name !== '' && name.length <= 20 && REGEXP_SPECIAL.test(name) && model !== '' &&  model.length <= 20 && REGEXP_SPECIAL.test(model) ) {

      return await existsSensorModel(name, model)
        .then((response) => {
          setChecked({result: response, message: response ? '이미 등록된 수질 센서 모델입니다.' : '사용하실 수 있는 수질 센서 모델입니다.'});
          setCheckedInfo(name + model);
        })
        .catch(() => {
          setChecked({result: null, message: '나중에 다시 시도해 주세요.'});
        });
    }
  };

  const onChecked = (value: string) => {
    setValue('name', value);
  };

  const onSubmit = async (data: Props) => {
    const alarmLowerLimit = data.alarm === 'UPPER_LIMIT' ? null : data.alarmLowerLimit;
    const alarmUpperLimit = data.alarm === 'LOWER_LIMIT' ? null : data.alarmUpperLimit;

    if ( create ) {
      if ( checked === null || checkedInfo !== data.name + data.model ) {
        setChecked({result: null, message: '중복 체크 후 진행하실 수 있습니다.'});
      } else if ( checked.result === false ) {
        return await createSensorModel(data.name, data.model, data.waterQualityItem, data.unit, data.alarm, alarmLowerLimit, alarmUpperLimit)
          .then(() => navigate('/iot/sensor-model', {state: {submit: true}}))
          .catch(() => setMessage('요청하신 수질 센서 모델을 등록할 수 없습니다.\n나중에 다시 시도해 주세요.'));
      }
    } else {
      const modifyName = state.name !== data.name ? true : false;
      return await updateSensorModel(state.waterQualitySensorModelId, modifyName, data.name, data.waterQualityItem, data.unit, data.alarm, alarmLowerLimit, alarmUpperLimit)
        .then(() => navigate('/iot/sensor-model', {state: {submit: true}}))
        .catch(() => setMessage('요청하신 수질 센서 모델을 수정할 수 없습니다.\n나중에 다시 시도해 주세요.'));
    }
  };

  return (
    <Container>
      <Breadcrumbs
        heading={`수질 센서 모델 ${create ? '등록' : '수정'}`}
        links={[
          {name: 'IoT 장비 관리'},
          {name: '수질 센서 모델', link: '/iot/sensor-model'},
          {name: `수질 센서 모델 ${create ? '등록' : '수정'}`}
        ]}
      />

      <form onSubmit={ handleSubmit(onSubmit) } noValidate>

        { create ? (
          <TextField
            type='text'
            name='name'
            label='수질 센서'
            essential={true}
            placeholder='예) MLSS / MLSS2'
            description='20자 이하 한글, 영문 대소문자, 숫자, 특수기호 !@&()-_+[]{}. 를 사용해 주세요.'
            control={control}
            maxLength={20}
          />
        ) : (
          <ButtonField
            type='text'
            name='name'
            label='수질 센서'
            control={control}
            disabled={true}
            action={
              <TertiaryButton type='button' onClick={() => setDialog(true)}>{ create ? '중복 체크' : '수정' }</TertiaryButton>
            }
          />
        )}

        <TextField
          type='text'
          name='model'
          label='모델명'
          essential={create ? true : false}
          disabled={create ? false : true}
          description={create ? '20자 이하 한글, 영문 대소문자, 숫자, 특수기호 !@&()-_+[]{}. 를 사용해 주세요.' : null}
          control={control}
          maxLength={20}
        />

        <div>
          <TertiaryButton type='button' disabled={create ? false : true} style={{'width': '100%'}} onClick={() => handleChecked()}>중복 확인</TertiaryButton>

          { checked.message &&
            <FormMessage type={checked.result === false ? 'info' : 'error'} style={{'marginTop': '4px'}}>{ checked.message }</FormMessage>
          }
        </div>
              <SelectField
                label='수질 항목'
                name='waterQualityItem'
                control={control}
                option={[
                  {value: 'PH', name: 'PH'},
                  {value: 'CD', name: 'CD'},
                  {value: 'RC', name: 'RC'},
                  {value: 'BOD', name: 'BOD'},
                  {value: 'TOC', name: 'TOC'},
                  {value: 'TN', name: 'TN'},
                  {value: 'TP', name: 'TP'},
                ]}
                essential
              />
        
        <TextField type='text' name='unit' label='단위' control={control} essential={true} maxLength={10} />

        <div>
          <Label><span>* </span>알람 범위</Label>
          <MultipleFields>
            <SelectField
              name='alarm'
              option={[
                {value: 'OUT_OF_RANGE', name: '범위'},
                {value: 'LOWER_LIMIT', name: '하한'},
                {value: 'UPPER_LIMIT', name: '상한'}
              ]}
              control={control}
            />

            <div>
              <TextField type='number' name='alarmLowerLimit' placeholder='하한' disabled={watch('alarm') === 'UPPER_LIMIT' ? true : false} control={control} step={0.1} />
              <p>~</p>
              <TextField type='number' name='alarmUpperLimit' placeholder='상한' disabled={watch('alarm') === 'LOWER_LIMIT' ? true : false} control={control} step={0.1} />
            </div>
          </MultipleFields>

          { formError &&
            <FormMessage>{ formError }</FormMessage>
          }
        </div>

        <Message $visible={message ? true : false}>{ message }</Message>

        <ButtonWrapper>
          <PrimaryButton type='submit'>{create ? '등록' : '수정'}</PrimaryButton>
          <TertiaryButton type='button' onClick={() => navigate('/iot/sensor-model')}>취소</TertiaryButton>
        </ButtonWrapper>
      </form>

      { dialog &&
        <UpdateDialog
          handleClose={handleClose}
          name={getValues('name')}
          onChecked={onChecked}
        />
      }
    </Container>
  );
}

// ----------------------------------------------------------------------

const updateSchema = yup.object().shape({
  name: yup.string()
    .required()
    .trim()
    .max(20, '형식을 확인해 주세요.')
    .matches(REGEXP_SPECIAL, '형식을 확인해 주세요.'),
});

interface DialogProps {
  handleClose: () => void;
  name: string;
  onChecked: (value: string) => void
};

function UpdateDialog({ handleClose, name, onChecked }: DialogProps) {

  const {
    control,
    getValues,
    setFocus,
    setError,
    clearErrors,
    handleSubmit
  } = useForm({
    resolver: yupResolver(updateSchema),
    defaultValues: {
      name: name || ''
    }
  });

  const onSubmit = async (data: {name?: string}) => {
    onChecked(data.name);
    handleClose();
  };

  return (
    <Dialog
      title='조직 수정'
      handleClose={handleClose}
    >
      <form onSubmit={ handleSubmit(onSubmit) }>
        <TextField
          type='text'
          name='name'
          label='수질 센서'
          essential={true}
          placeholder='예) MLSS / MLSS2'
          description='20자 이하 한글, 영문 대소문자, 숫자, 특수기호 !@&()-_+[]{}. 를 사용해 주세요.'
          control={control}
          maxLength={20}
        />
        <ButtonWrapper>
          <PrimaryButton type='submit'>수정</PrimaryButton>
          <TertiaryButton type='button' onClick={() => handleClose()}>취소</TertiaryButton>
        </ButtonWrapper>
      </form>
    </Dialog>
  );
}